import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getProfile } from '@/features/profiles/lib/profileQueries';
import { ProfileAvatar } from '@/features/profiles/components/profile-avatar';
import { saveExtraction } from '@/features/timeline/lib/save';
import { newEventId } from '@/features/timeline/lib/eventId';
import type { AIFlagCode, EventType, ExtractPrescriptionMedication, Profile, VitalSigns } from '@/types/db';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: Profile }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

interface EventTypeOption {
  value: EventType;
  label: string;
  helper: string;
}

const EVENT_TYPE_OPTIONS: EventTypeOption[] = [
  {
    value: 'doctor_visit',
    label: 'Doctor visit',
    helper: 'A consultation or check-up.',
  },
  {
    value: 'prescription',
    label: 'Prescription',
    helper: 'Medications prescribed by a doctor.',
  },
  {
    value: 'lab_report',
    label: 'Lab report',
    helper: 'Blood test, urine test, or other lab results.',
  },
  {
    value: 'vaccine',
    label: 'Vaccine',
    helper: 'Immunization, shot, or booster.',
  },
  {
    value: 'vitals',
    label: 'Vitals',
    helper: 'Heart rate, blood pressure, blood sugar, weight, height.',
  },
  {
    value: 'scan',
    label: 'Scan',
    helper: 'X-ray, ultrasound, MRI, or other imaging (no photo).',
  },
  {
    value: 'other',
    label: 'Other',
    helper: 'Anything else worth remembering.',
  },
];

const VACCINE_CHIPS = [
  'Flu',
  'COVID-19',
  'MMR',
  'Hepatitis B',
  'Td/Tdap',
  'Chickenpox',
  'Pneumococcal',
  'HPV',
];

const DOSE_CHIPS = ['1st dose', '2nd dose', '3rd dose', 'Booster'];

function computeBMI(
  weightStr: string,
  heightStr: string,
): { value: number; label: string; colorClass: string } | null {
  const w = parseFloat(weightStr);
  const h = parseFloat(heightStr);
  if (!w || !h || h === 0) return null;
  const bmi = w / (h / 100) ** 2;
  const rounded = Math.round(bmi * 10) / 10;
  if (rounded < 18.5)
    return { value: rounded, label: 'Underweight', colorClass: 'text-flag-info' };
  if (rounded < 25)
    return { value: rounded, label: 'Normal', colorClass: 'text-[#2E7D32]' };
  if (rounded < 30)
    return { value: rounded, label: 'Overweight', colorClass: 'text-flag-warn' };
  return { value: rounded, label: 'Obese', colorClass: 'text-flag-block' };
}

const VALID_EVENT_TYPES: EventType[] = ['doctor_visit', 'prescription', 'lab_report', 'vaccine', 'vitals', 'scan', 'other'];

export function ManualEntryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [eventType, setEventType] = useState<EventType>(() => {
    const typeParam = searchParams.get('type') as EventType | null;
    return typeParam && VALID_EVENT_TYPES.includes(typeParam) ? typeParam : 'doctor_visit';
  });
  const [title, setTitle] = useState('');
  const [eventDate, setEventDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [doctorName, setDoctorName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Type-specific fields
  const [specialty, setSpecialty] = useState('');
  const [doseNumber, setDoseNumber] = useState('');
  const [heartRate, setHeartRate] = useState('');
  const [systolicBp, setSystolicBp] = useState('');
  const [diastolicBp, setDiastolicBp] = useState('');
  const [bloodSugar, setBloodSugar] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [medications, setMedications] = useState([
    { drug_name: '', dosage: '', frequency: '', duration_days: '' },
  ]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getProfile(id)
      .then((profile) => {
        if (cancelled) return;
        if (!profile) {
          setState({ kind: 'missing' });
          return;
        }
        setState({ kind: 'ready', profile });
      })
      .catch((err) => {
        console.error('[manual-entry] load profile failed', err);
        if (cancelled) return;
        setState({
          kind: 'error',
          message: "Couldn't load this profile.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  function resetTypeFields() {
    setSpecialty('');
    setDoseNumber('');
    setHeartRate('');
    setSystolicBp('');
    setDiastolicBp('');
    setBloodSugar('');
    setWeightKg('');
    setHeightCm('');
    setDiagnosis('');
    setMedications([{ drug_name: '', dosage: '', frequency: '', duration_days: '' }]);
  }

  function handleTypeChange(newType: EventType) {
    setEventType(newType);
    resetTypeFields();
    setTitle('');
    setNotes('');
    setDoctorName('');
    setClinicName('');
    setError(null);
  }

  function hasAtLeastOneVital(): boolean {
    return [heartRate, systolicBp, diastolicBp, bloodSugar, weightKg, heightCm].some(
      (v) => v.trim() !== '' && !isNaN(parseFloat(v)),
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== 'ready' || submitting) return;

    if (eventType === 'vitals') {
      if (!hasAtLeastOneVital()) {
        setError('Fill in at least one measurement.');
        return;
      }
    } else if (eventType === 'prescription') {
      if (!medications.some((m) => m.drug_name.trim())) {
        setError('Add at least one medication name.');
        return;
      }
    } else {
      const trimmedTitle = title.trim();
      if (!trimmedTitle) {
        setError(
          'Give this a short title so you recognize it on the timeline.',
        );
        return;
      }
    }

    setError(null);
    setSubmitting(true);
    const eventId = newEventId();

    let finalTitle = title.trim() || null;
    let finalNotes = notes.trim() || null;
    let finalDiagnosis: string | null = null;
    let vitalSigns: VitalSigns | undefined;
    let finalMedications: ExtractPrescriptionMedication[] = [];

    if (eventType === 'vitals') {
      finalTitle = finalTitle || 'Vitals';
      const bmiResult = computeBMI(weightKg, heightCm);
      vitalSigns = {};
      if (heartRate.trim()) vitalSigns.heart_rate = parseFloat(heartRate);
      if (systolicBp.trim()) vitalSigns.systolic_bp = parseFloat(systolicBp);
      if (diastolicBp.trim()) vitalSigns.diastolic_bp = parseFloat(diastolicBp);
      if (bloodSugar.trim()) vitalSigns.blood_sugar = parseFloat(bloodSugar);
      if (weightKg.trim()) vitalSigns.weight_kg = parseFloat(weightKg);
      if (heightCm.trim()) vitalSigns.height_cm = parseFloat(heightCm);
      if (bmiResult) vitalSigns.bmi = bmiResult.value;
    }

    if (eventType === 'vaccine' && doseNumber) {
      finalNotes = `Dose: ${doseNumber}${finalNotes ? `\n${finalNotes}` : ''}`;
    }

    if (eventType === 'prescription') {
      finalMedications = medications
        .filter((m) => m.drug_name.trim())
        .map((m) => ({
          drug_name: m.drug_name.trim(),
          dosage: m.dosage.trim() || null,
          frequency: m.frequency.trim() || null,
          duration_days: m.duration_days ? parseInt(m.duration_days, 10) : null,
          flags: [] as AIFlagCode[],
        }));
    }

    if (eventType === 'lab_report') {
      finalDiagnosis = diagnosis.trim() || null;
    }

    try {
      await saveExtraction({
        profileId: state.profile.id,
        eventId,
        eventType,
        title: finalTitle,
        eventDate: eventDate || null,
        doctorName: doctorName.trim() || null,
        doctorSpecialty: eventType === 'doctor_visit' ? (specialty.trim() || null) : null,
        clinicName: clinicName.trim() || null,
        clinicAddress: null,
        clinicPhone: null,
        insuranceCompany: null,
        insuranceClaim: null,
        diagnosis: finalDiagnosis,
        notes: finalNotes,
        vitalSigns,
        aiFlags: [],
        medications: finalMedications,
        attachments: [],
      });
      navigate(`/profile/${state.profile.id}`, {
        replace: true,
        state: { justSavedEventId: eventId },
      });
    } catch (err) {
      console.error('[manual-entry] save failed', err);
      setError("Couldn't save. Please try again.");
      setSubmitting(false);
    }
  }

  const backLink = id ? `/profile/${id}` : '/';
  const bmiDisplay = computeBMI(weightKg, heightCm);

  const canSubmit =
    eventType === 'vitals'       ? hasAtLeastOneVital() :
    eventType === 'prescription' ? medications.some((m) => m.drug_name.trim() !== '') :
    !!title.trim();

  return (
    <main className="min-h-screen safe-top safe-bottom">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          <Link
            to={backLink}
            aria-label="Back"
            className="h-10 w-10 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>
          <h1 className="text-lg font-bold truncate flex-1">Add manually</h1>
        </div>
      </header>

      <section className="px-4 py-6 max-w-md mx-auto">
        {state.kind === 'loading' && (
          <div
            className="card p-6 h-40 animate-pulse bg-canvas"
            aria-busy="true"
          />
        )}

        {state.kind === 'missing' && (
          <div className="card p-6 text-center">
            <p className="font-medium mb-2">Profile not found</p>
            <Link to="/home" className="btn-secondary inline-flex">
              Back to family
            </Link>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="card p-6 text-center">
            <p className="text-flag-block mb-2">{state.message}</p>
            <Link to={backLink} className="btn-secondary inline-flex">
              Back
            </Link>
          </div>
        )}

        {state.kind === 'ready' && (
          <form onSubmit={handleSubmit} className="card p-5">
            <div className="flex items-center gap-3 mb-5">
              <ProfileAvatar
                name={state.profile.name}
                color={state.profile.avatar_color}
              />
              <div>
                <p className="text-xs text-ink-muted">For</p>
                <p className="font-bold">{state.profile.name}</p>
              </div>
            </div>

            <label className="label" htmlFor="event-type">
              Type
            </label>
            <select
              id="event-type"
              value={eventType}
              onChange={(e) => handleTypeChange(e.target.value as EventType)}
              className="input mb-1"
            >
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink-subtle mb-4">
              {EVENT_TYPE_OPTIONS.find((o) => o.value === eventType)?.helper}
            </p>

            {/* ── Type-specific fields ── */}

            {eventType === 'doctor_visit' && (
              <>
                <label className="label" htmlFor="title">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  required
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. GP check-up, follow-up after surgery"
                  maxLength={140}
                />

                <label className="label" htmlFor="specialty">
                  Specialty{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="specialty"
                  type="text"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Cardiology, Pediatrics"
                  maxLength={100}
                />

                <label className="label" htmlFor="event-date">
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input mb-4"
                />

                <label className="label" htmlFor="doctor-name">
                  Doctor{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="doctor-name"
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Dr. Sarah Ahmed"
                  maxLength={200}
                />

                <label className="label" htmlFor="clinic-name">
                  Clinic or hospital{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="clinic-name"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Cairo Medical Center"
                  maxLength={200}
                />

                <label className="label" htmlFor="notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input mb-5 min-h-[96px] resize-y"
                  placeholder="What happened, what was recommended…"
                  maxLength={2000}
                />
              </>
            )}

            {eventType === 'vaccine' && (
              <>
                <label className="label" htmlFor="title">
                  Vaccine name
                </label>
                <input
                  id="title"
                  type="text"
                  required
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input mb-2"
                  placeholder="e.g. Flu shot, COVID-19 booster"
                  maxLength={140}
                />
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {VACCINE_CHIPS.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setTitle(name)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        title === name
                          ? 'bg-evt-vax-soft text-evt-vax-ink border-evt-vax/30'
                          : 'bg-canvas text-ink-muted border-border hover:border-border-strong'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>

                <label className="label">
                  Dose{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {DOSE_CHIPS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() =>
                        setDoseNumber(doseNumber === d ? '' : d)
                      }
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        doseNumber === d
                          ? 'bg-evt-vax-soft text-evt-vax-ink border-evt-vax/30'
                          : 'bg-canvas text-ink-muted border-border hover:border-border-strong'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>

                <label className="label" htmlFor="event-date">
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input mb-4"
                />

                <label className="label" htmlFor="doctor-name">
                  Doctor / Nurse{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="doctor-name"
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Dr. Sarah Ahmed"
                  maxLength={200}
                />

                <label className="label" htmlFor="clinic-name">
                  Clinic{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="clinic-name"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Cairo Medical Center"
                  maxLength={200}
                />

                <label className="label" htmlFor="notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input mb-5 min-h-[96px] resize-y"
                  placeholder="Batch number, side effects…"
                  maxLength={2000}
                />
              </>
            )}

            {eventType === 'vitals' && (
              <>
                <label className="label" htmlFor="heart-rate">
                  Heart rate{' '}
                  <span className="text-ink-subtle font-normal">bpm</span>
                </label>
                <input
                  id="heart-rate"
                  type="number"
                  inputMode="numeric"
                  autoFocus
                  value={heartRate}
                  onChange={(e) => setHeartRate(e.target.value)}
                  className="input mb-4"
                  placeholder="72"
                  min={20}
                  max={300}
                />

                <label className="label">
                  Blood pressure{' '}
                  <span className="text-ink-subtle font-normal">mmHg</span>
                </label>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={systolicBp}
                    onChange={(e) => setSystolicBp(e.target.value)}
                    className="input flex-1"
                    placeholder="120"
                    aria-label="Systolic"
                    min={50}
                    max={300}
                  />
                  <span className="text-ink-muted font-medium">/</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={diastolicBp}
                    onChange={(e) => setDiastolicBp(e.target.value)}
                    className="input flex-1"
                    placeholder="80"
                    aria-label="Diastolic"
                    min={20}
                    max={200}
                  />
                </div>

                <label className="label" htmlFor="blood-sugar">
                  Blood sugar{' '}
                  <span className="text-ink-subtle font-normal">mg/dL</span>
                </label>
                <input
                  id="blood-sugar"
                  type="number"
                  inputMode="numeric"
                  value={bloodSugar}
                  onChange={(e) => setBloodSugar(e.target.value)}
                  className="input mb-4"
                  placeholder="95"
                  min={10}
                  max={900}
                />

                <label className="label" htmlFor="weight-kg">
                  Weight{' '}
                  <span className="text-ink-subtle font-normal">kg</span>
                </label>
                <input
                  id="weight-kg"
                  type="number"
                  inputMode="decimal"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  className="input mb-4"
                  placeholder="70"
                  min={1}
                  max={500}
                  step="0.1"
                />

                <label className="label" htmlFor="height-cm">
                  Height{' '}
                  <span className="text-ink-subtle font-normal">cm</span>
                </label>
                <input
                  id="height-cm"
                  type="number"
                  inputMode="numeric"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  className="input mb-4"
                  placeholder="175"
                  min={30}
                  max={300}
                />

                {bmiDisplay && (
                  <div className="rounded-xl bg-canvas border border-border p-3 mb-4">
                    <p className="text-xs text-ink-muted mb-0.5">BMI</p>
                    <p className="text-lg font-bold text-ink">
                      {bmiDisplay.value}{' '}
                      <span className={`text-sm font-semibold ${bmiDisplay.colorClass}`}>
                        {bmiDisplay.label}
                      </span>
                    </p>
                  </div>
                )}

                <label className="label" htmlFor="event-date">
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input mb-4"
                />

                <label className="label" htmlFor="notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input mb-5 min-h-[72px] resize-y"
                  placeholder="Context, fasting state…"
                  maxLength={2000}
                />
              </>
            )}

            {eventType === 'prescription' && (
              <>
                <label className="label" htmlFor="title">
                  Title{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="title"
                  type="text"
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Prescription from Dr. Ahmed"
                  maxLength={140}
                />

                <label className="label" htmlFor="event-date">
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input mb-4"
                />

                <label className="label" htmlFor="doctor-name">
                  Doctor{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="doctor-name"
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Dr. Sarah Ahmed"
                  maxLength={200}
                />

                <label className="label" htmlFor="clinic-name">
                  Clinic or hospital{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="clinic-name"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Cairo Medical Center"
                  maxLength={200}
                />

                <label className="label">
                  Medications
                </label>
                <div className="flex flex-col gap-3 mb-3">
                  {medications.map((med, idx) => (
                    <div key={idx} className="rounded-xl border border-border bg-canvas p-3 flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={med.drug_name}
                          onChange={(e) => {
                            const next = [...medications];
                            next[idx] = { ...next[idx], drug_name: e.target.value };
                            setMedications(next);
                          }}
                          className="input flex-1"
                          placeholder="Drug name (required)"
                          maxLength={200}
                          autoFocus={idx === medications.length - 1 && idx > 0}
                        />
                        {medications.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setMedications(medications.filter((_, i) => i !== idx))}
                            className="h-9 w-9 flex items-center justify-center rounded-full text-ink-muted hover:text-flag-block shrink-0"
                            aria-label="Remove medication"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={med.dosage}
                          onChange={(e) => {
                            const next = [...medications];
                            next[idx] = { ...next[idx], dosage: e.target.value };
                            setMedications(next);
                          }}
                          className="input"
                          placeholder="Dosage (e.g. 500 mg)"
                          maxLength={100}
                        />
                        <input
                          type="text"
                          value={med.frequency}
                          onChange={(e) => {
                            const next = [...medications];
                            next[idx] = { ...next[idx], frequency: e.target.value };
                            setMedications(next);
                          }}
                          className="input"
                          placeholder="Frequency (e.g. Twice daily)"
                          maxLength={100}
                        />
                      </div>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={med.duration_days}
                        onChange={(e) => {
                          const next = [...medications];
                          next[idx] = { ...next[idx], duration_days: e.target.value };
                          setMedications(next);
                        }}
                        className="input"
                        placeholder="Duration (days)"
                        min={1}
                        max={3650}
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setMedications([...medications, { drug_name: '', dosage: '', frequency: '', duration_days: '' }])
                  }
                  className="w-full py-2 rounded-xl border border-dashed border-border text-sm font-medium text-ink-muted hover:border-border-strong hover:text-ink transition-colors mb-4"
                >
                  + Add medication
                </button>

                <label className="label" htmlFor="notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input mb-5 min-h-[72px] resize-y"
                  placeholder="Additional instructions, allergies noted…"
                  maxLength={2000}
                />
              </>
            )}

            {eventType === 'lab_report' && (
              <>
                <label className="label" htmlFor="title">
                  Test / panel name
                </label>
                <input
                  id="title"
                  type="text"
                  required
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. CBC panel, Liver function test"
                  maxLength={140}
                />

                <label className="label" htmlFor="event-date">
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input mb-4"
                />

                <label className="label" htmlFor="doctor-name">
                  Requesting doctor{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="doctor-name"
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Dr. Sarah Ahmed"
                  maxLength={200}
                />

                <label className="label" htmlFor="clinic-name">
                  Lab / clinic{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="clinic-name"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Alpha Medical Lab"
                  maxLength={200}
                />

                <label className="label" htmlFor="diagnosis">
                  Report conclusion{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="diagnosis"
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  className="input mb-4 min-h-[72px] resize-y"
                  placeholder="Lab's printed impression or interpretation…"
                  maxLength={2000}
                />

                <label className="label" htmlFor="notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input mb-5 min-h-[72px] resize-y"
                  placeholder="Key values, flagged results…"
                  maxLength={2000}
                />
              </>
            )}

            {eventType === 'scan' && (
              <>
                <label className="label" htmlFor="title">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  required
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Chest X-ray, Abdominal ultrasound"
                  maxLength={140}
                />

                <label className="label" htmlFor="event-date">
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input mb-4"
                />

                <label className="label" htmlFor="doctor-name">
                  Doctor (radiologist){' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="doctor-name"
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Dr. Sarah Ahmed"
                  maxLength={200}
                />

                <label className="label" htmlFor="clinic-name">
                  Clinic{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="clinic-name"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Cairo Medical Center"
                  maxLength={200}
                />

                <label className="label" htmlFor="notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input mb-5 min-h-[96px] resize-y"
                  placeholder="Findings, impressions…"
                  maxLength={2000}
                />
              </>
            )}

            {eventType === 'other' && (
              <>
                <label className="label" htmlFor="title">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  required
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input mb-4"
                  placeholder="Short name"
                  maxLength={140}
                />

                <label className="label" htmlFor="event-date">
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input mb-4"
                />

                <label className="label" htmlFor="doctor-name">
                  Doctor{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="doctor-name"
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Dr. Sarah Ahmed"
                  maxLength={200}
                />

                <label className="label" htmlFor="clinic-name">
                  Clinic or hospital{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="clinic-name"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="input mb-4"
                  placeholder="e.g. Cairo Medical Center"
                  maxLength={200}
                />

                <label className="label" htmlFor="notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input mb-5 min-h-[96px] resize-y"
                  placeholder="What happened, what was recommended…"
                  maxLength={2000}
                />
              </>
            )}

            {error && (
              <p className="text-sm text-flag-block mb-4" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || !canSubmit}
              className="btn-primary w-full"
            >
              {submitting ? 'Saving…' : 'Save to timeline'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
